/**
 * S-13 «Реклама» и S-14 «Кампания». Источник — тот же конверт, что у агента «Реклама»:
 * цифры экрана и утреннего отчёта совпадают. Кнопок «Пополнить», «Пауза», «Изменить ставку» нет:
 * Штаб читает кабинет и сам его не меняет — действие делается в кабинете WB.
 */
import { fetchAds, fetchCampaign, type Campaign } from '../api/sections.js'
import { Icon } from '../components/icons/Icon.js'
import { Failed, Loading, NoShop, Empty, useData, useScreenHead } from './common.js'
import { Kv, Num, Proposals, Sect, ToneTag } from './ui.js'

type Open = (id: import('../router/screens.js').ScreenId, param?: string) => void

function CampaignRow({ c, onOpen }: { c: Campaign; onOpen: () => void }): React.JSX.Element {
  return (
    <button type="button" className="row row-tap camp-row" data-testid={'campaign-' + c.advert_id} onClick={onOpen}>
      <span className="row-main">
        <span className="row-title">{c.name}</span>
        <span className="row-meta">{c.how_display}</span>
        <span className="camp-tags"><ToneTag tone={c.status.tone} word={c.status.word} /></span>
      </span>
      <span className="row-val camp-val">
        <span className="num"><span className="vh">расход </span><Num v={c.spend} /></span>
        <span className="num-sub"><span className="vh">ДРР </span>{c.drr.display != null ? <>ДРР {c.drr.display}</> : <Num v={c.drr} />}</span>
      </span>
      <Icon name="nav-next" className="ic-chev row-end" />
    </button>
  )
}

export function ScreenAds({ open }: { open: Open }): React.JSX.Element {
  const { s, reload } = useData(fetchAds)
  useScreenHead(undefined, null)
  if (s.kind === 'loading') return <Loading label="Загрузка рекламы" rows={6} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  const d = s.data
  if (d.shop === null) return <NoShop reason={d.reason} />
  const t = d.totals
  return (
    <div data-testid="screen-body">
      <Sect title="Итог за неделю" aside={d.period_display} />
      <section className="card">
        <Kv testid="ads-totals" rows={[
          { label: 'Расход на рекламу', v: t.spend, strong: true },
          { label: 'Заказы с рекламы', v: t.orders, sub: t.orders_share.display != null ? `${t.orders_share.display} всех заказов магазина` : null },
          { label: 'Заказано с рекламы на сумму', v: t.revenue },
          { label: 'ДРР по заказам', v: t.drr, strong: true,
            sub: [t.drr_delta.display != null ? `${t.drr_delta.display} к прошлой неделе` : null,
              t.target_drr.display != null ? `цель ${t.target_drr.display}` : 'цель не задана'].filter(Boolean).join(' · ') },
        ]} />
        <p className="card-foot">{d.revenue_base_display}</p>
      </section>

      <Sect title="Деньги на рекламу" aside="как в кабинете WB" />
      <section className="card">
        <Kv testid="ads-balance" rows={[
          { label: 'Счёт', v: d.balance.account },
          { label: 'Баланс', v: d.balance.netting, sub: 'взаимозачёт: реклама оплачивается из будущих продаж' },
          { label: 'Бонусы', v: d.balance.bonus },
          { label: 'Расход в день, в среднем', v: d.balance.avg_daily_spend },
          { label: 'Счёта и баланса хватит на', v: d.balance.cover_days, sub: 'при среднем расходе за неделю' },
        ]} />
      </section>

      <Sect title="Кампании" aside={`${d.campaigns.length}`} />
      {d.campaigns.length === 0
        ? <section className="card empty-card"><Empty icon="agent-ads" title="Рабочих кампаний нет" text="Здесь будут кампании, которые идут или стоят на паузе." /></section>
        : <section className="card" data-testid="campaigns">
            {d.campaigns.map((c) => <CampaignRow key={c.advert_id} c={c} onOpen={() => open('S-14', String(c.advert_id))} />)}
          </section>}
      {(d.benchmarks.drr_median.display != null || d.benchmarks.cpo_median.display != null) && (
        <p className="hint">
          Обычно у магазина: ДРР <Num v={d.benchmarks.drr_median} />, цена заказа <Num v={d.benchmarks.cpo_median} /> — медиана полных недель.
        </p>
      )}
    </div>
  )
}

export function ScreenCampaign({ id, onReport }: { id: string; onReport: (rid: string) => void }): React.JSX.Element {
  const { s, reload } = useData(() => fetchCampaign(id))
  const c = s.kind === 'ok' && s.data.shop !== null ? s.data.campaign : null
  useScreenHead(c?.name, c === null ? null : `${c.status.word} · ${c.how_display}`)
  if (s.kind === 'loading') return <Loading label="Загрузка кампании" rows={6} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  const d = s.data
  if (d.shop === null || c === null) return <NoShop reason={d.reason} />
  return (
    <div data-testid="screen-body">
      {c.insufficient !== null && (
        <div className="note" data-testid="campaign-insufficient">
          <Icon name="section-blind" size={20} />
          <span>Мало данных для выводов: {c.insufficient}.</span>
        </div>
      )}
      <Sect title="Показатели" aside={d.period_display} />
      <section className="card">
        <Kv testid="campaign-kpi" rows={[
          { label: 'Расход', v: c.spend, strong: true, sub: c.daily_spend.display != null ? `${c.daily_spend.display} в день` : null },
          { label: 'Заказы', v: c.orders },
          { label: 'Заказано на сумму', v: c.orders_rub },
          { label: 'ДРР по заказам', v: c.drr, strong: true },
          { label: 'Цена заказа', v: c.cpo },
          { label: 'Показы', v: c.views },
          { label: 'Клики', v: c.clicks, sub: c.ctr.display != null ? `кликабельность ${c.ctr.display}` : null },
          { label: 'Цена клика', v: c.cpc },
          { label: 'Конверсия в заказ', v: c.cr },
          { label: 'Бюджет кампании', v: c.budget },
        ]} />
      </section>

      <Sect title="Ставка" />
      <section className="card">
        <Kv testid="campaign-bid" rows={[
          { label: 'Сейчас', v: c.bid, strong: true },
          { label: 'Минимальная у Wildberries', v: c.bid_min },
          { label: 'Рекомендует Штаб', v: c.bid_recommended, sub: c.bid_recommended.display == null ? c.bid_recommended_reason : null },
        ]} />
        <p className="card-foot">Ставку меняют в кабинете WB: Штаб читает данные кабинета и сам ничего в нём не меняет.</p>
      </section>

      {d.products.length > 0 && <>
        <Sect title="Товары кампании" aside={`${d.products.length}`} />
        <section className="card" data-testid="campaign-products">
          {d.products.map((p) => (
            <div className="row" key={p.nm_id}>
              <span className="row-main">
                <span className="row-title">{p.title ?? `Артикул WB ${p.nm_id}`}</span>
                <span className="row-meta">
                  Артикул WB {p.nm_id}
                  {p.days_of_stock.display != null && <> · запаса на {p.days_of_stock.display}</>}
                  {p.minus_phrases.display != null && <> · минус-фраз {p.minus_phrases.display}</>}
                </span>
              </span>
              <span className="row-val"><span className="num"><span className="vh">в наличии </span><Num v={p.stock} /></span></span>
            </div>
          ))}
        </section>
      </>}

      {d.keywords.length > 0 && <>
        <Sect title="Поисковые фразы" aside="по расходу" />
        <section className="card" data-testid="campaign-keywords">
          {d.keywords.map((k, i) => (
            <div className="row" key={i}>
              <span className="row-main">
                <span className="row-title">{k.phrase}</span>
                <span className="row-meta">клики <Num v={k.clicks} /> · заказы <Num v={k.orders} /></span>
                {k.minus_candidate && <span className="camp-tags"><ToneTag tone="attn" word="Кандидат в минус-фразы" /></span>}
              </span>
              <span className="row-val"><span className="num"><span className="vh">расход </span><Num v={k.spend} /></span></span>
            </div>
          ))}
          <p className="card-foot">{d.keywords_note}</p>
        </section>
      </>}

      <Proposals items={d.proposals} onReport={onReport} />
    </div>
  )
}
